import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { createButtonPressAnimation } from "../../../../utilities";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { openSettings } from "../../../../State/actions";
import { selectMenuActive } from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { soundManager } from "../../../../SoundManager";

const ButtonSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig, device, orientation, dimensions } =
    useDimensions();
  const menuActive = useSelector(selectMenuActive);

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);
  const animationRef = useRef(null);

  // Track if press animation is currently running (prevents rapid click shrinking)
  const isPressAnimatingRef = useRef(false);

  // Refs to track current dimensions for animation (no re-renders)
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const isInitializedRef = useRef(false);

  // Track previous menuActive state to detect ACTUAL changes (not just re-renders)
  const previousMenuActiveRef = useRef(menuActive);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  const texture = PIXI.Assets.get(ASSET_NAME.icon_settings);

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Calculate responsive button position based on device and orientation - memoized
  const buttonPosition = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 4.45,
        };
      } else {
        // landscape
        return {
          x: buttonConfig.width * 0.8,
          y: dimensions.height - buttonConfig.height * 4.25,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 4.4,
        };
      } else {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 4.45,
        };
      }
    } else {
      return {
        x: buttonConfig.width,
        y: dimensions.height - buttonConfig.height * 4.5,
      };
    }
  }, [
    device,
    orientation,
    dimensions.height,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  const handleClick = () => {
    soundManager.play(SOUND_NAME.button_click);

    // Add press animation before opening settings
    if (buttonSpriteRef.current && !isPressAnimatingRef.current) {
      isPressAnimatingRef.current = true;
      createButtonPressAnimation(buttonSpriteRef.current, {
        targetWidth: buttonConfig.width,
        targetHeight: buttonConfig.height,
        onComplete: () => {
          isPressAnimatingRef.current = false;
        },
      });
    }

    dispatch(openSettings());
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [buttonSpriteRef, brightnessFilter]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Initialize sprite dimensions on mount ONLY - using sprite's own flag to prevent re-initialization
  useEffect(() => {
    if (buttonSpriteRef.current && !buttonSpriteRef.current._initialized) {
      buttonSpriteRef.current.width = 0;
      buttonSpriteRef.current.height = 0;
      dimensionsRef.current.width = 0;
      dimensionsRef.current.height = 0;
      // Store original dimensions on sprite for reference
      buttonSpriteRef.current._originalWidth = buttonConfig.width;
      buttonSpriteRef.current._originalHeight = buttonConfig.height;
      buttonSpriteRef.current._initialized = true;
      isInitializedRef.current = true;
    }
  }, [buttonConfig.width, buttonConfig.height]);

  // Update stored dimensions on resize (no animation)
  useEffect(() => {
    if (!buttonSpriteRef.current || !isInitializedRef.current) return;

    const buttonSprite = buttonSpriteRef.current;

    // Update stored original dimensions if they changed (resize)
    if (buttonSprite._originalWidth !== buttonConfig.width || buttonSprite._originalHeight !== buttonConfig.height) {
      buttonSprite._originalWidth = buttonConfig.width;
      buttonSprite._originalHeight = buttonConfig.height;

      // If menu is open, update sprite size immediately without animation
      if (menuActive && buttonSprite.width > 0) {
        buttonSprite.width = buttonConfig.width;
        buttonSprite.height = buttonConfig.height;
        dimensionsRef.current.width = buttonConfig.width;
        dimensionsRef.current.height = buttonConfig.height;
      }
    }
  }, [buttonConfig.width, buttonConfig.height, menuActive]);

  // Width and height animation effect - ONLY triggers when menuActive ACTUALLY CHANGES
  useEffect(() => {
    if (!buttonSpriteRef.current || !isInitializedRef.current) return;

    // Check if menuActive actually changed (not just a re-render)
    if (previousMenuActiveRef.current === menuActive) {
      return; // Skip animation if state didn't change
    }
    previousMenuActiveRef.current = menuActive;

    const buttonSprite = buttonSpriteRef.current;

    // Stop any existing animation
    if (animationRef.current) {
      anime.remove(animationRef.current);
    }

    // Get current dimensions from sprite (preserve current state)
    const currentWidth = buttonSprite.width || 0;
    const currentHeight = buttonSprite.height || 0;

    // Update ref with current values
    dimensionsRef.current.width = currentWidth;
    dimensionsRef.current.height = currentHeight;

    if (menuActive) {
      // Animate from current size to target width and height (last to appear - 200ms delay)
      animationRef.current = anime({
        targets: dimensionsRef.current,
        width: buttonSprite._originalWidth,
        height: buttonSprite._originalHeight,
        duration: 200,
        delay: 200,
        easing: "easeOutQuad",
        update: () => {
          // Directly update sprite dimensions without causing re-renders
          if (buttonSprite) {
            buttonSprite.width = dimensionsRef.current.width;
            buttonSprite.height = dimensionsRef.current.height;
          }
        },
      });
    } else {
      // Animate from current size back to 0 (first to close - no delay)
      animationRef.current = anime({
        targets: dimensionsRef.current,
        width: 0,
        height: 0,
        duration: 200,
        delay: 0,
        easing: "easeInQuad",
        update: () => {
          // Directly update sprite dimensions without causing re-renders
          if (buttonSprite) {
            buttonSprite.width = dimensionsRef.current.width;
            buttonSprite.height = dimensionsRef.current.height;
          }
        },
      });
    }

    // Cleanup function to stop animation on unmount
    return () => {
      if (animationRef.current) {
        anime.remove(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuActive]); // ONLY menuActive triggers animation

  // Clean up hover effects on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
    };
  }, []);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width],
  );

  return (
    <Container
      name="secondary_button_settings"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={2}
    >
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_settings_interactive_area"
        eventMode={menuActive ? "static" : "none"}
        cursor="pointer"
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      {/* Note: width/height are controlled by animation, not props */}
      <Sprite
        ref={buttonSpriteRef}
        texture={texture}
        name="button_settings_icon"
        anchor={0.5}
        x={0}
        y={0}
        eventMode="none"
        accessibleTitle={t("settings")}
        accessibleHint={t("settings")}
        resolution={2}
      />
    </Container>
  );
};

export default ButtonSettings;
